import { useEffect, useRef } from 'react'
import { Chart, registerables } from 'chart.js'
import { colors, fonts, fmt } from './UI'
import { useTheme } from '../lib/theme'
import Hint from './Hint'

Chart.register(...registerables)

// Map budget categories to 50/30/20 buckets.
// Priority: an explicit `bucket` tag on the category wins; otherwise fall back
// to name-matching; anything unrecognised counts as Wants.
export function categoriseToBuckets(budgets, totalIncome) {
  const NEEDS = ['Housing', 'Utilities', 'Transport', 'Health', 'Groceries', 'Insurance']
  const WANTS = ['Entertainment', 'Clothing', 'Subscriptions', 'Other']
  const SAVINGS = ['Savings', 'Investments']

  let needs = 0, wants = 0, savings = 0

  budgets.forEach(b => {
    const spent = b.spent || b.budgeted || 0
    if (b.bucket === 'needs') needs += spent
    else if (b.bucket === 'savings') savings += spent
    else if (b.bucket === 'wants') wants += spent
    else if (NEEDS.some(n => b.category?.includes(n))) needs += spent
    else if (SAVINGS.some(s => b.category?.includes(s))) savings += spent
    else wants += spent
  })

  const total = needs + wants + savings || 1
  const income = totalIncome || total

  return {
    needs: { amount: needs, pct: Math.round(needs / income * 100), target: 50, label: 'Needs (Essentials)' },
    wants: { amount: wants, pct: Math.round(wants / income * 100), target: 30, label: 'Wants' },
    savings: { amount: savings, pct: Math.round(savings / income * 100), target: 20, label: 'Savings & Investments' },
  }
}

function statusText(actual, target, key) {
  const diff = actual - target
  if (key === 'savings') {
    // For savings, under target is bad
    if (diff >= 0) return { text: `${actual}% - on target`, color: colors.green, ok: true }
    return { text: `${actual}% - ${Math.abs(diff)}% below the ${target}% target`, color: colors.red, ok: false }
  }
  // For needs and wants, over target is bad
  if (diff <= 0) return { text: `${actual}% - on target`, color: colors.green, ok: true }
  return { text: `${actual}% - ${diff}% over the ${target}% target`, color: colors.red, ok: false }
}

export default function FinancialBlueprint({ budgets, totalIncome, compact }) {
  const { themeId } = useTheme()
  const youRef = useRef(null)
  const recRef = useRef(null)
  const youChart = useRef(null)
  const recChart = useRef(null)

  const buckets = categoriseToBuckets(budgets, totalIncome)
  const BUCKET_COLORS = [colors.gold, '#7b68ee', colors.green]
  const LABELS = ['Needs (Essentials)', 'Wants', 'Savings & Investments']
  const TARGETS = [50, 30, 20]
  const yourData = [buckets.needs.pct, buckets.wants.pct, buckets.savings.pct]
  const recData = [50, 30, 20]

  useEffect(() => {
    const makeChart = (ref, data, title) => {
      if (!ref.current) return null
      return new Chart(ref.current, {
        type: 'doughnut',
        data: {
          labels: LABELS,
          datasets: [{ data, backgroundColor: BUCKET_COLORS, borderWidth: 0, hoverOffset: 4 }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: '65%',
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${ctx.raw}%` } }
          }
        }
      })
    }

    if (youChart.current) youChart.current.destroy()
    if (recChart.current) recChart.current.destroy()
    youChart.current = makeChart(youRef, yourData, 'You')
    recChart.current = makeChart(recRef, recData, 'Recommended')

    return () => {
      if (youChart.current) youChart.current.destroy()
      if (recChart.current) recChart.current.destroy()
    }
  }, [budgets, totalIncome, themeId])

  if (compact) {
    // Compact version for dashboard health card
    const offTrack = Object.values(buckets).filter(b => b.pct > b.target)
    return (
      <div style={{ padding: '10px 0' }}>
        {Object.entries(buckets).map(([key, b]) => {
          const diff = b.pct - b.target
          const ok = key === 'savings' ? diff >= 0 : diff <= 0
          return (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid rgba(255,255,255,0.04)`, fontFamily: fonts.body }}>
              <span style={{ fontSize: '14px', fontWeight: 500, color: colors.muted }}>{b.label}</span>
              <span style={{ fontSize: '14px', color: ok ? colors.green : colors.red, fontWeight: 500 }}>
                {b.pct}% {ok ? '✓' : `(+${diff}%)`}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div>
      {/* Two donuts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
        {[
          { ref: youRef, title: 'Your allocation', data: yourData },
          { ref: recRef, title: 'Recommended (50/30/20)', data: recData },
        ].map(({ ref, title, data }) => (
          <div key={title} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.gold, marginBottom: '10px' }}>{title}</div>
            <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto' }}>
              <canvas ref={ref} />
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {LABELS.map((label, i) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: fonts.body, fontSize: '14px', color: colors.muted }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: BUCKET_COLORS[i], flexShrink: 0 }} />
            {label}
          </div>
        ))}
      </div>

      {/* Breakdown */}
      {Object.entries(buckets).map(([key, b], i) => {
        const status = statusText(b.pct, b.target, key)
        const monthlyDiff = Math.abs(b.amount - (totalIncome * b.target / 100))
        return (
          <div key={key} style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '2px', marginBottom: '8px', borderLeft: `3px solid ${BUCKET_COLORS[i]}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <span style={{ fontSize: '15px', color: colors.text, fontFamily: fonts.body, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                {b.label}
                {key === 'needs' && <Hint title="Needs (Essentials)" text="Bills, rent, mortgage, groceries, utilities, transport, insurance - things you must pay. Target: 50% of income." />}
                {key === 'wants' && <Hint title="Wants" text="Eating out, entertainment, subscriptions, clothing - things you choose to spend on. Target: 30% of income." />}
                {key === 'savings' && <Hint title="Savings & Investments" text="Money going toward savings goals, emergency fund, and investments. Target: 20% of income. Below target is flagged in red." />}
              </span>
              <span style={{ fontSize: '13px', fontWeight: 500, color: status.color, fontFamily: fonts.body, fontWeight: 600 }}>{b.pct}% <span style={{ fontSize: '14px', color: colors.muted }}>/ {b.target}% target</span></span>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: status.color, fontFamily: fonts.body }}>
              {status.text}
              {!status.ok && totalIncome > 0 && (
                <span style={{ color: colors.muted }}> - redirecting {fmt(monthlyDiff)}/mo would fix this</span>
              )}
            </div>
          </div>
        )
      })}

      <div style={{ fontSize: '14px', color: colors.mutedDeep, fontFamily: fonts.body, marginTop: '12px', lineHeight: 1.6 }}>
        The 50/30/20 rule is a general guideline, not financial advice. Your ideal split depends on your income, debts and goals.
      </div>
    </div>
  )
}
